// externals
import type { Reducer } from 'redux';
import produce, { type Draft } from 'immer';

import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import type { BaggageEntry, Comparison } from '@makemydeal/dr-dash-bff-types';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { findPaymentGridPayloadInBaggage, type PaymentGridBaggagePayload } from '@makemydeal/dr-platform-shared';
import { getActiveComparison, mergeActiveComparison } from '../utils/multiScenarioUtils';
import { OFFER_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';
import {
    OUT_OF_BAND_BANNER_DISPLAYED,
    OUT_OF_BAND_BANNER_HIDDEN,
    UPDATE_ACTIVE_COMPARISON
} from '../actionTypes/multiScenarioActionTypes';

type ComparisonState = {
    comparisonList: Comparison[];
    activeComparison: Comparison | undefined;
    isMoreRecentVersionAvailable: boolean;
    outOfBandBannerDisplayed: boolean;
    /**
     * US2041815: the compressed payment grid item for the active comparison, kept as posted.
     *
     * Held here rather than decompressed on arrival because it is per scenario: a reader takes the one
     * entry it needs. The bytes are the same ones the browser wrote, so nothing is re-derived.
     */
    paymentGridPayload?: PaymentGridBaggagePayload;
};

export const COMPARISON_INITIAL_STATE: ComparisonState = {
    comparisonList: [],
    activeComparison: undefined,
    isMoreRecentVersionAvailable: false,
    outOfBandBannerDisplayed: false
};

export type ComparisonReducer = Reducer<ComparisonState>;

/**
 * US2041815: keeps the stored grid item, and keeps it only when the incoming baggage has one.
 *
 * All three actions that refresh the active comparison land here, but only the bootstrap response
 * carries the grid item — `saveScenarios` puts it on the patch payload, not on the response baggage. An
 * unconditional assignment therefore wiped a perfectly good payload on every save.
 */
const setPaymentGridPayload = (draft: Draft<ComparisonState>, baggage: unknown, comparisonVersionId?: string) => {
    const payload = findPaymentGridPayloadInBaggage(baggage as BaggageEntry[] | undefined, comparisonVersionId);
    if (payload) draft.paymentGridPayload = payload;
};

export const reducer: Reducer<ComparisonState> = (state: ComparisonState = COMPARISON_INITIAL_STATE, action: AnyFSA) =>
    produce(state, (draft: Draft<ComparisonState>) => {
        switch (action.type) {
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
                if (Array.isArray(action.payload.baggage)) {
                    const comparisonBaggage = action.payload.baggage.find(
                        ({ name }: BaggageEntry) => name === 'deal360.comparison'
                    );
                    if (comparisonBaggage) {
                        const comparisonList = comparisonBaggage.value.content;
                        draft.comparisonList = comparisonList;
                        const activeComparison = getActiveComparison(comparisonList, action.payload.offer.dealExchangeVersionId);
                        if (activeComparison) {
                            draft.activeComparison = activeComparison;
                            draft.isMoreRecentVersionAvailable =
                                activeComparison.dealXgVersionId !== action.payload.offer.dealExchangeVersionId;
                            // The grid item is named for the comparison's own deal version, which is how it is
                            // found without knowing anything about the scenarios inside it.
                            //
                            // Assigned only when one is found. The save-offer response baggage carries the
                            // comparison but not the grid item, so overwriting unconditionally cleared the
                            // payload immediately after a successful save — and the next scenario load then
                            // re-priced instead of restoring.
                            setPaymentGridPayload(draft, action.payload.baggage, activeComparison.dealXgVersionId);
                        }
                    }
                }
                break;
            }
            case OFFER_SAVE_SUCCESS: {
                if (Array.isArray(action.payload.baggage)) {
                    const comparisonBaggage = action.payload.baggage.find(
                        ({ name }: BaggageEntry) => name === 'deal360.comparison'
                    );
                    if (comparisonBaggage) {
                        const comparisonList = comparisonBaggage.value.content;
                        draft.comparisonList = comparisonList;
                        const activeComparison = getActiveComparison(comparisonList, action.payload.dealXgVersion);
                        if (activeComparison) {
                            draft.activeComparison = activeComparison;
                            draft.isMoreRecentVersionAvailable = activeComparison.dealXgVersionId !== action.payload.dealXgVersion;
                            setPaymentGridPayload(draft, action.payload.baggage, activeComparison.dealXgVersionId);
                        }
                    }
                }
                break;
            }
            case OUT_OF_BAND_BANNER_DISPLAYED: {
                draft.outOfBandBannerDisplayed = true;
                break;
            }
            case OUT_OF_BAND_BANNER_HIDDEN: {
                draft.outOfBandBannerDisplayed = false;
                break;
            }
            case UPDATE_ACTIVE_COMPARISON: {
                if (Array.isArray(action.payload.baggage)) {
                    const comparisonBaggage = action.payload.baggage.find(
                        ({ name }: BaggageEntry) => name === 'deal360.comparison'
                    );
                    if (comparisonBaggage) {
                        const comparisonList = comparisonBaggage.value.content;
                        draft.comparisonList = comparisonList;
                        const newActiveComparison = getActiveComparison(comparisonList, action.payload.offer.dealExchangeVersionId);
                        if (newActiveComparison) {
                            const currentActiveComparison = state.activeComparison;
                            const activeComparison = mergeActiveComparison(newActiveComparison, currentActiveComparison);
                            draft.activeComparison = activeComparison;
                            draft.isMoreRecentVersionAvailable =
                                activeComparison.dealXgVersionId !== action.payload.offer.dealExchangeVersionId;
                            setPaymentGridPayload(draft, action.payload.baggage, activeComparison.dealXgVersionId);
                        }
                    }
                }
                break;
            }
        }
    });
